import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Plus, Pencil, Trash2, X, Check } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { RESOURCE_SCHEMAS } from "./schemas";
import ImageUpload from "@/components/admin/ImageUpload";
import VideoUpload from "@/components/admin/VideoUpload";
import IconPicker from "@/components/admin/IconPicker";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const MultiImage = ({ value, onChange, label }) => {
  const arr = Array.isArray(value) ? value : [];
  return (
    <div>
      <label className="text-sm font-medium text-foreground mb-1 block">{label}</label>
      <div className="flex flex-wrap gap-2 mb-2">
        {arr.map((img, i) => (
          <div key={`${img}-${i}`} className="relative">
            <img src={fileUrl(img)} alt="" className="w-16 h-16 rounded-lg object-cover border border-border" />
            <button type="button" onClick={() => onChange(arr.filter((_, idx) => idx !== i))} className="absolute -top-1.5 -right-1.5 bg-destructive text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
          </div>
        ))}
      </div>
      <ImageUpload value="" onChange={(url) => url && onChange([...arr, url])} label="Adicionar foto" />
    </div>
  );
};

const DEFAULT_ON = ["available", "active", "contact_enabled", "wa_enabled", "details_enabled", "button_enabled"];

const WA_PRESETS = [
  "Falar pelo WhatsApp", "Entrar em contato", "Solicitar orçamento",
  "Agendar atendimento", "Marcar horário", "Consultar disponibilidade",
  "Pedir pelo WhatsApp", "Chamar no WhatsApp",
];

const Field = ({ f, value, onChange, businesses, categories }) => {
  const common = "w-full px-3 py-2 rounded-lg border border-border bg-background outline-none focus:border-primary text-sm";
  switch (f.type) {
    case "textarea":
      return <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={f.big ? 6 : 3} className={common} data-testid={`field-${f.name}`} placeholder={f.placeholder} />;
    case "number":
      return <input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} className={common} data-testid={`field-${f.name}`} />;
    case "price":
      return <input type="number" step="0.01" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} className={common} data-testid={`field-${f.name}`} />;
    case "date":
      return <input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className={common} data-testid={`field-${f.name}`} />;
    case "image":
      return <ImageUpload value={value} onChange={onChange} />;
    case "video":
      return <VideoUpload value={value} onChange={onChange} />;
    case "images":
      return <MultiImage value={value} onChange={onChange} label={f.label} />;
    case "icon":
      return <IconPicker value={value} onChange={onChange} />;
    case "switch":
      return <Switch checked={!!value} onCheckedChange={onChange} data-testid={`field-${f.name}`} />;
    case "wa_button": {
      const presets = f.presets || WA_PRESETS;
      return (
        <div>
          <input list={`wa-presets-${f.name}`} type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} className={common} placeholder={f.placeholder || "Pedir pelo WhatsApp"} data-testid={`field-${f.name}`} />
          <datalist id={`wa-presets-${f.name}`}>
            {presets.map((p) => <option key={p} value={p} />)}
          </datalist>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {presets.map((p) => (
              <button key={p} type="button" onClick={() => onChange(p)} className={`text-xs rounded-full px-2.5 py-1 border transition-colors ${value === p ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary text-foreground"}`}>{p}</button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-1">Escolha uma opção ou digite um texto personalizado.</p>
        </div>
      );
    }
    case "select":
      return (
        <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={common} data-testid={`field-${f.name}`}>
          <option value="">Selecione...</option>
          {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    case "select-category":
      return (
        <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={common} data-testid={`field-${f.name}`}>
          <option value="">Selecione...</option>
          {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
      );
    case "select-business":
      return (
        <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={common} data-testid={`field-${f.name}`}>
          <option value="">Selecione...</option>
          {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      );
    default:
      return <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} className={common} placeholder={f.placeholder} data-testid={`field-${f.name}`} />;
  }
};

const ResourceManager = () => {
  const { resource } = useParams();
  const schema = RESOURCE_SCHEMAS[resource];
  const [items, setItems] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/${resource}`).then((r) => setItems(r.data)).catch(() => {});
  }, [resource]);

  useEffect(() => {
    load();
    api.get("/admin/businesses").then((r) => setBusinesses(r.data)).catch(() => {});
    api.get("/admin/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, [load]);

  if (!schema) return <p>Recurso inválido</p>;

  const TAGS_FIELD = { name: "tags", label: "Tags (palavras para busca, separe por vírgula)", type: "text", placeholder: "ex: turismo, passeio, lazer" };
  const formFields = [...schema.fields, TAGS_FIELD];

  const openNew = () => {
    const defaults = { active: true };
    schema.fields.forEach((f) => {
      if (f.type === "switch" && DEFAULT_ON.includes(f.name)) defaults[f.name] = true;
    });
    setForm(defaults);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    const next = { ...item };
    schema.fields.forEach((f) => {
      if (f.type === "switch" && DEFAULT_ON.includes(f.name) && next[f.name] === undefined) next[f.name] = true;
    });
    setForm(next);
    setDialogOpen(true);
  };

  const save = async () => {
    const missing = schema.fields.filter((f) => f.required && !form[f.name]);
    if (missing.length) {
      toast.error(`Preencha: ${missing.map((m) => m.label).join(", ")}`);
      return;
    }
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/admin/${resource}/${form.id}`, form);
        toast.success("Atualizado");
      } else {
        await api.post(`/admin/${resource}`, form);
        toast.success("Criado");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este item?")) return;
    try {
      await api.delete(`/admin/${resource}/${id}`);
      toast.success("Excluído");
      load();
    } catch (e) {
      toast.error("Erro ao excluir");
    }
  };

  const cell = (item, col) => {
    const v = item[col];
    if (typeof v === "boolean") return v ? <Check className="w-4 h-4 text-secondary" /> : <X className="w-4 h-4 text-muted-foreground" />;
    if (col === "business_id") return businesses.find((b) => b.id === v)?.name || "-";
    return v ?? "-";
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold font-heading text-slate-900">{schema.label}</h1>
          <p className="text-sm text-muted-foreground">{items.length} itens cadastrados</p>
        </div>
        <button onClick={openNew} data-testid="add-item-btn" className="flex items-center gap-2 bg-primary text-primary-foreground font-semibold rounded-full px-4 py-2.5 text-sm active:scale-95 transition-transform">
          <Plus className="w-4 h-4" /> Adicionar
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                {schema.columns.map((c) => <th key={c} className="px-4 py-3 font-semibold">{c}</th>)}
                <th className="px-4 py-3 font-semibold text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((item) => (
                <tr key={item.id} data-testid={`row-${item.id}`} className="hover:bg-slate-50">
                  {schema.columns.map((c) => <td key={c} className="px-4 py-3 text-slate-700 max-w-[200px] truncate">{cell(item, c)}</td>)}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openEdit(item)} data-testid={`edit-${item.id}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(item.id)} data-testid={`delete-${item.id}`} className="p-2 rounded-lg hover:bg-red-50 text-destructive"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={schema.columns.length + 1} className="px-4 py-10 text-center text-muted-foreground">Nenhum item. Clique em Adicionar.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{form.id ? "Editar" : "Novo"} {schema.singular}</DialogTitle>
          </DialogHeader>
          <DialogDescription className="sr-only">Formulário para cadastrar ou editar {schema.singular}.</DialogDescription>
          <div className="space-y-4 mt-2">
            {formFields.map((f) => (
              <div key={f.name} className={f.type === "switch" ? "flex items-center justify-between" : ""}>
                {f.type !== "image" && f.type !== "images" && f.type !== "icon" && (
                  <label className="text-sm font-medium text-foreground block mb-1">
                    {f.label}{f.required && <span className="text-destructive"> *</span>}
                  </label>
                )}
                <Field f={f} value={form[f.name]} onChange={(v) => setForm((p) => ({ ...p, [f.name]: v }))} businesses={businesses} categories={categories} />
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-5 sticky bottom-0 bg-card pt-3">
            <button onClick={() => setDialogOpen(false)} className="flex-1 border border-border rounded-full py-2.5 text-sm font-semibold hover:bg-slate-100 transition-colors">Cancelar</button>
            <button onClick={save} disabled={saving} data-testid="save-item-btn" className="flex-1 bg-primary text-primary-foreground rounded-full py-2.5 text-sm font-semibold active:scale-95 transition-transform disabled:opacity-60">
              {saving ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResourceManager;
